// Common provider types.
// Single source of the ProviderKey value list — the Zod schema in
// lib/core/crm/types/common.ts derives from this tuple (no hand-typed duplicate),
// and lib/constants/email-providers.ts pins its email subset against ProviderKey.
// These are canonical integration CONFIG keys, NOT raw legacy provider-template slugs
// (Outlook's slug "outlook" normalizes to "outlook-oauth" — see email-providers.ts).
export const PROVIDER_KEY_VALUES = [
  "google-mail",
  "outlook-oauth",
  "slack-oauth",
  "communication",
  "professional-email",
] as const;

export type ProviderKey = (typeof PROVIDER_KEY_VALUES)[number];

// Common connection types
export interface BaseConnection {
  id: string;
  provider: string;
  providerConfigKey: string;
  connectionId: string;
  email: string | null;
  createdAt: string;
  updatedAt: string;
  lastFetchedAt: string;
  status: string;
  isShared?: boolean; // Whether this connection is shared with the current user
}

export interface CreateConnectionRequest {
  provider: string;
  providerConfigKey: string;
  connectionId: string;
  email?: string;
  metadata?: any;
}

// Common message types
// Note: Message type is now defined in lib/crm/types/index.ts as the canonical type
// Use that instead of MessageBase for consistency

// Common API response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T = any> {
  data: T[];
  totalCount: number;
  hasMore: boolean;
  nextPageToken?: string;
}

// Common error types
export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// Common request parameter types
export interface PaginationParams {
  page?: number;
  limit?: number;
  skip?: number;
}

export interface FilterParams {
  filter?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// Common provider configuration types
export interface ProviderConfig {
  providerConfigKey: string;
  apiEndpoint: string;
  supportedFilters?: string[];
  supportedSortFields?: string[];
}

// Common authentication types
export interface AuthUser {
  id: string;
  email: string;
  userType: string;
}

// Common validation types
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// Common logging types
export interface LogContext {
  userId?: string;
  provider?: string;
  connectionId?: string;
  messageId?: string;
  operation?: string;
}

// Common timestamp types
export interface TimestampFields {
  createdAt: string;
  updatedAt: string;
  lastFetchedAt?: string;
}

// Common metadata types
export interface MetadataFields {
  metadata?: Record<string, any>;
  tags?: string[];
  labels?: string[];
}

// Message action types (used by client hooks and server message-action-handler)
export type MessageAction =
  | "archive"
  | "delete"
  | "mark_as_read"
  | "mark_as_unread"
  | "mark_important"
  | "mark_unimportant"
  | "mark_spam"
  | "reply"
  | "forward"
  | "move";

export interface MessageActionPayload {
  [key: string]: unknown;
}
